import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const UserManagement = ({ currentUser }) => {
  const callerIsSuperuser = currentUser?.isSuperuser || false;
  const [users, setUsers] = useState([]);
  const [options, setOptions] = useState({ operations: [], units: [], departments: [], operationUnits: [] });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');

  // Estado do modal de edição
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    isSuperuser: false,
    isSimple: false,
    operationIds: [],
    unitIds: [],
    departmentIds: [],
  });
  const [saving, setSaving] = useState(false);

  // Buscas dentro do modal de permissões
  const [searchOps, setSearchOps] = useState('');
  const [searchUnits, setSearchUnits] = useState('');
  const [searchDepts, setSearchDepts] = useState('');

  // Estado do modal de criação de usuário
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    isSuperuser: false,
    isSimple: !currentUser?.isSuperuser,
    operationIds: [],
    unitIds: [],
    departmentIds: [],
  });
  const [createSearchOps, setCreateSearchOps] = useState('');
  const [createSearchUnits, setCreateSearchUnits] = useState('');
  const [createSearchDepts, setCreateSearchDepts] = useState('');

  // Estado do modal de reset de senha
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, optionsRes] = await Promise.all([
        axios.get('/users'),
        axios.get('/users/options'),
      ]);

      if (usersRes.data.success) setUsers(usersRes.data.data);
      if (optionsRes.data.success) setOptions(optionsRes.data.data);
    } catch (error) {
      console.error('Error loading users:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar usuários' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  // Filtrar usuários da lista
  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return users.filter((user) => {
      if (term) {
        const searchable = `${user.username} ${user.firstName} ${user.lastName} ${user.email}`.toLowerCase();
        if (!searchable.includes(term)) return false;
      }
      if (filterStatus === 'active' && !user.isActive) return false;
      if (filterStatus === 'inactive' && user.isActive) return false;
      if (filterRole === 'superuser' && !user.isSuperuser) return false;
      if (filterRole === 'regular' && (user.isSuperuser || user.isSimple)) return false;
      if (filterRole === 'simple' && !user.isSimple) return false;
      return true;
    });
  }, [users, searchTerm, filterStatus, filterRole]);

  // Stats
  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.isActive).length;
  const superUsers = users.filter(u => u.isSuperuser).length;
  const simpleUsers = users.filter(u => u.isSimple).length;

  const getUserTypeLabel = (user) => {
    if (user.isSuperuser) return 'Super';
    if (user.isSimple) return 'Simples';
    return 'Regular';
  };

  const getUserTypeIcon = (user) => {
    if (user.isSuperuser) return 'fa-user-shield';
    if (user.isSimple) return 'fa-user';
    return 'fa-user-tie';
  };

  // ============================================================
  // Filtragem em cascata: Operações → Unidades → Departamentos
  // ============================================================

  // Operações filtradas por busca
  const filteredOperations = useMemo(() => {
    const term = searchOps.toLowerCase().trim();
    if (!term) return options.operations;
    return options.operations.filter(op =>
      op.name.toLowerCase().includes(term) || (op.code || '').toLowerCase().includes(term)
    );
  }, [options.operations, searchOps]);

  // Unidades filtradas: se há operações selecionadas, mostrar apenas unidades vinculadas via operationUnits
  const filteredUnits = useMemo(() => {
    const term = searchUnits.toLowerCase().trim();
    let units = options.units;

    // Filtrar por operações selecionadas
    if (editForm.operationIds.length > 0 && options.operationUnits?.length > 0) {
      const linkedUnitIds = new Set(
        options.operationUnits
          .filter(ou => editForm.operationIds.includes(ou.operation_id))
          .map(ou => ou.unit_id)
      );
      units = units.filter(u => linkedUnitIds.has(u.id));
    }

    // Filtrar por busca
    if (term) {
      units = units.filter(u =>
        u.name.toLowerCase().includes(term) || (u.code || '').toLowerCase().includes(term)
      );
    }

    return units;
  }, [options.units, options.operationUnits, editForm.operationIds, searchUnits]);

  // Departamentos filtrados: por operações e unidades selecionadas
  const filteredDepartments = useMemo(() => {
    const term = searchDepts.toLowerCase().trim();
    let depts = options.departments;

    // Filtrar por operações selecionadas
    if (editForm.operationIds.length > 0) {
      depts = depts.filter(d => editForm.operationIds.includes(d.operation_id));
    }

    // Filtrar por unidades selecionadas
    if (editForm.unitIds.length > 0) {
      depts = depts.filter(d => editForm.unitIds.includes(d.unit_id));
    }

    // Filtrar por busca
    if (term) {
      depts = depts.filter(d =>
        d.name.toLowerCase().includes(term) ||
        (d.operation_name || '').toLowerCase().includes(term) ||
        (d.unit_name || '').toLowerCase().includes(term)
      );
    }

    return depts;
  }, [options.departments, editForm.operationIds, editForm.unitIds, searchDepts]);

  // ============================================================
  // Filtragem em cascata para modal de CRIAÇÃO
  // ============================================================
  const createFilteredOperations = useMemo(() => {
    const term = createSearchOps.toLowerCase().trim();
    if (!term) return options.operations;
    return options.operations.filter(op =>
      op.name.toLowerCase().includes(term) || (op.code || '').toLowerCase().includes(term)
    );
  }, [options.operations, createSearchOps]);

  const createFilteredUnits = useMemo(() => {
    const term = createSearchUnits.toLowerCase().trim();
    let units = options.units;
    if (createForm.operationIds.length > 0 && options.operationUnits?.length > 0) {
      const linkedUnitIds = new Set(
        options.operationUnits
          .filter(ou => createForm.operationIds.includes(ou.operation_id))
          .map(ou => ou.unit_id)
      );
      units = units.filter(u => linkedUnitIds.has(u.id));
    }
    if (term) {
      units = units.filter(u =>
        u.name.toLowerCase().includes(term) || (u.code || '').toLowerCase().includes(term)
      );
    }
    return units;
  }, [options.units, options.operationUnits, createForm.operationIds, createSearchUnits]);

  const createFilteredDepartments = useMemo(() => {
    const term = createSearchDepts.toLowerCase().trim();
    let depts = options.departments;
    if (createForm.operationIds.length > 0) {
      depts = depts.filter(d => createForm.operationIds.includes(d.operation_id));
    }
    if (createForm.unitIds.length > 0) {
      depts = depts.filter(d => createForm.unitIds.includes(d.unit_id));
    }
    if (term) {
      depts = depts.filter(d =>
        d.name.toLowerCase().includes(term) ||
        (d.operation_name || '').toLowerCase().includes(term) ||
        (d.unit_name || '').toLowerCase().includes(term)
      );
    }
    return depts;
  }, [options.departments, createForm.operationIds, createForm.unitIds, createSearchDepts]);

  // ============================================================
  // Criar usuário
  // ============================================================
  const openCreateModal = () => {
    setShowCreateModal(true);
    setCreateForm({
      username: '', email: '', firstName: '', lastName: '', password: '',
      isSuperuser: false, isSimple: !callerIsSuperuser, operationIds: [], unitIds: [], departmentIds: [],
    });
    setCreateSearchOps('');
    setCreateSearchUnits('');
    setCreateSearchDepts('');
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm({
      username: '', email: '', firstName: '', lastName: '', password: '',
      isSuperuser: false, isSimple: !callerIsSuperuser, operationIds: [], unitIds: [], departmentIds: [],
    });
    setCreateSearchOps('');
    setCreateSearchUnits('');
    setCreateSearchDepts('');
  };

  const handleCreateUser = async () => {
    if (!createForm.username.trim()) {
      showMessage('error', 'Username é obrigatório');
      return;
    }
    if (!createForm.password || createForm.password.length < 8) {
      showMessage('error', 'A senha deve ter pelo menos 8 caracteres');
      return;
    }

    try {
      setSaving(true);
      const response = await axios.post('/users', createForm);
      if (response.data.success) {
        showMessage('success', `Usuário ${createForm.username} criado com sucesso!`);
        closeCreateModal();
        await loadData();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Erro ao criar usuário';
      showMessage('error', errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const toggleCreateArrayItem = (field, id) => {
    setCreateForm(prev => {
      const arr = prev[field];
      const exists = arr.includes(id);
      return { ...prev, [field]: exists ? arr.filter(item => item !== id) : [...arr, id] };
    });
  };

  const selectAllCreateFiltered = (field, filteredItems) => {
    const filteredIds = filteredItems.map(item => item.id);
    setCreateForm(prev => {
      const current = new Set(prev[field]);
      const allSelected = filteredIds.every(id => current.has(id));
      if (allSelected) {
        return { ...prev, [field]: prev[field].filter(id => !filteredIds.includes(id)) };
      } else {
        const merged = new Set([...prev[field], ...filteredIds]);
        return { ...prev, [field]: [...merged] };
      }
    });
  };

  const allCreateFilteredSelected = (field, filteredItems) => {
    if (filteredItems.length === 0) return false;
    return filteredItems.every(item => createForm[field].includes(item.id));
  };

  // ============================================================
  // Abrir / Fechar modal
  // ============================================================
  const openEditModal = (user) => {
    setEditingUser(user);
    setEditForm({
      isSuperuser: user.isSuperuser,
      isSimple: user.isSimple || false,
      operationIds: user.operations.map(o => o.id),
      unitIds: user.units.map(u => u.id),
      departmentIds: user.departments.map(d => d.id),
    });
    setSearchOps('');
    setSearchUnits('');
    setSearchDepts('');
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditForm({ isSuperuser: false, isSimple: false, operationIds: [], unitIds: [], departmentIds: [] });
    setSearchOps('');
    setSearchUnits('');
    setSearchDepts('');
  };

  // ============================================================
  // Salvar permissões
  // ============================================================
  const savePermissions = async () => {
    if (!editingUser) return;
    try {
      setSaving(true);
      const response = await axios.put(`/users/${editingUser.id}/permissions`, editForm);
      if (response.data.success) {
        showMessage('success', `Permissões de ${editingUser.username} atualizadas com sucesso!`);
        closeEditModal();
        await loadData();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Erro ao salvar permissões';
      showMessage('error', errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Toggle status do usuário
  const toggleUserStatus = async (user) => {
    const action = user.isActive ? 'desativar' : 'ativar';
    if (!window.confirm(`Tem certeza que deseja ${action} o usuário "${user.username}"?`)) return;

    try {
      const response = await axios.put(`/users/${user.id}/status`, { isActive: !user.isActive });
      if (response.data.success) {
        showMessage('success', `Usuário ${user.username} ${user.isActive ? 'desativado' : 'ativado'} com sucesso!`);
        await loadData();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || `Erro ao ${action} usuário`;
      showMessage('error', errorMsg);
    }
  };

  // Reset de senha
  const handleResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) return;
    try {
      setSaving(true);
      const response = await axios.put(`/users/${resetPasswordUser.id}/reset-password`, { newPassword });
      if (response.data.success) {
        showMessage('success', `Senha de ${resetPasswordUser.username} redefinida com sucesso!`);
        setResetPasswordUser(null);
        setNewPassword('');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Erro ao redefinir senha';
      showMessage('error', errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // Toggle item em array
  // ============================================================
  const toggleArrayItem = (field, id) => {
    setEditForm(prev => {
      const arr = prev[field];
      const exists = arr.includes(id);
      return {
        ...prev,
        [field]: exists ? arr.filter(item => item !== id) : [...arr, id],
      };
    });
  };

  // Selecionar todos os itens filtrados visíveis
  const selectAllFiltered = (field, filteredItems) => {
    const filteredIds = filteredItems.map(item => item.id);
    setEditForm(prev => {
      const current = new Set(prev[field]);
      const allSelected = filteredIds.every(id => current.has(id));

      if (allSelected) {
        // Desmarcar todos os filtrados
        return {
          ...prev,
          [field]: prev[field].filter(id => !filteredIds.includes(id)),
        };
      } else {
        // Marcar todos os filtrados (mantendo os que já estão selecionados fora do filtro)
        const merged = new Set([...prev[field], ...filteredIds]);
        return {
          ...prev,
          [field]: [...merged],
        };
      }
    });
  };

  // Verificar se todos os filtrados estão selecionados
  const allFilteredSelected = (field, filteredItems) => {
    if (filteredItems.length === 0) return false;
    return filteredItems.every(item => editForm[field].includes(item.id));
  };

  // ============================================================
  // Render
  // ============================================================

  if (loading) {
    return (
      <div className="goals-list-page department-list-page">
        <div className="dl-container">
          <div className="dl-title-card">
            <h1><i className="fas fa-users-cog" /> Gerenciar Usuários</h1>
            <p>Carregando...</p>
          </div>
          <div className="dl-loading">
            <div className="spinner-border" role="status" aria-label="Carregando"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="goals-list-page department-list-page">
      <div className="dl-container">
        {/* Alert */}
        {message && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
            <div className="dl-alert-row">
              <span>{message.text}</span>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setMessage(null)} />
            </div>
          </div>
        )}

        {/* Header */}
        <div className="dl-title-card">
          <div className="dl-title-left">
            <h1><i className="fas fa-users-cog" /> Gerenciar Usuários</h1>
            <div className="dl-stats">
              <div className="dl-stat">
                <span className="dl-stat-num">{totalUsers}</span>
                <span className="dl-stat-label">Total</span>
              </div>
              <div className="dl-stat">
                <span className="dl-stat-num">{activeUsers}</span>
                <span className="dl-stat-label">Ativos</span>
              </div>
              {callerIsSuperuser && (
                <div className="dl-stat">
                  <span className="dl-stat-num">{superUsers}</span>
                  <span className="dl-stat-label">Super</span>
                </div>
              )}
              <div className="dl-stat">
                <span className="dl-stat-num">{simpleUsers}</span>
                <span className="dl-stat-label">Simples</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn dl-btn dl-btn-primary"
            onClick={openCreateModal}
          >
            <i className="fas fa-user-plus" /> Novo Usuário
          </button>
        </div>

        {/* Filters */}
        <div className="dl-filters-card">
          <div className="dl-filters-head">
            <h3><i className="fas fa-filter" /> Filtros</h3>
            <button
              type="button"
              className="btn dl-btn dl-btn-ghost"
              onClick={() => { setSearchTerm(''); setFilterStatus('all'); setFilterRole('all'); }}
            >
              <i className="fas fa-undo" /> Limpar
            </button>
          </div>
          <div className="dl-filters-grid">
            <div className="dl-field">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                className="form-control"
                placeholder="Nome, username ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="dl-field">
              <label className="form-label">Status</label>
              <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">Todos</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
              </select>
            </div>
            <div className="dl-field">
              <label className="form-label">Tipo</label>
              <select className="form-select" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                <option value="all">Todos</option>
                {callerIsSuperuser && <option value="superuser">Superusuários</option>}
                {callerIsSuperuser && <option value="regular">Regulares</option>}
                <option value="simple">Simples</option>
              </select>
            </div>
          </div>
        </div>

        {/* Users list */}
        {filteredUsers.length > 0 ? (
          <div className="um-users-list">
            {filteredUsers.map((user) => (
              <div key={user.id} className={`um-user-card ${!user.isActive ? 'um-user-inactive' : ''}`}>
                <div className="um-user-main">
                  <div className="um-user-info">
                    <div className="um-user-avatar">
                      <i className={`fas ${getUserTypeIcon(user)}`} />
                    </div>
                    <div className="um-user-details">
                      <div className="um-user-name">
                        {user.firstName || user.lastName
                          ? `${user.firstName} ${user.lastName}`.trim()
                          : user.username}
                        <span className="um-user-username">@{user.username}</span>
                      </div>
                      <div className="um-user-email">{user.email || 'Sem email'}</div>
                      <div className="um-user-badges">
                        <span className={`dl-pill ${user.isSuperuser ? 'dl-pill-active' : user.isSimple ? 'dl-pill-simple' : 'dl-pill-regular'}`}>
                          <i className={`fas ${getUserTypeIcon(user)}`} /> {getUserTypeLabel(user)}
                        </span>
                        <span className={`dl-pill ${user.isActive ? 'dl-pill-active' : 'dl-pill-inactive'}`}>
                          {user.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="um-user-permissions-summary">
                    {user.isSuperuser ? (
                      <span className="um-perm-tag um-perm-super">
                        <i className="fas fa-infinity" /> Acesso total
                      </span>
                    ) : (
                      <>
                        {user.operations.length > 0 && (
                          <span className="um-perm-tag">
                            <i className="fas fa-layer-group" /> {user.operations.length} operação(ões)
                          </span>
                        )}
                        {user.units.length > 0 && (
                          <span className="um-perm-tag">
                            <i className="fas fa-map-marker-alt" /> {user.units.length} unidade(s)
                          </span>
                        )}
                        {user.departments.length > 0 && (
                          <span className="um-perm-tag">
                            <i className="fas fa-building" /> {user.departments.length} departamento(s)
                          </span>
                        )}
                        {user.operations.length === 0 && user.units.length === 0 && user.departments.length === 0 && (
                          <span className="um-perm-tag um-perm-none">
                            <i className="fas fa-exclamation-triangle" /> Sem permissões
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="um-user-actions">
                  <button
                    type="button"
                    className="btn dl-btn dl-btn-primary dl-btn-sm"
                    onClick={() => openEditModal(user)}
                    title="Editar permissões"
                  >
                    <i className="fas fa-key" /> Permissões
                  </button>
                  <button
                    type="button"
                    className="btn dl-btn dl-btn-ghost dl-btn-sm"
                    onClick={() => { setResetPasswordUser(user); setNewPassword(''); }}
                    title="Redefinir senha"
                  >
                    <i className="fas fa-lock" /> Senha
                  </button>
                  <button
                    type="button"
                    className={`btn dl-btn ${user.isActive ? 'dl-btn-ghost' : 'dl-btn-success'} dl-btn-sm`}
                    onClick={() => toggleUserStatus(user)}
                    title={user.isActive ? 'Desativar' : 'Ativar'}
                  >
                    <i className={`fas fa-${user.isActive ? 'user-slash' : 'user-check'}`} />
                    {user.isActive ? 'Desativar' : 'Ativar'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="dl-empty">
            <i className="fas fa-users" />
            <h3>Nenhum usuário encontrado</h3>
            <p>Não há usuários que correspondam aos filtros aplicados.</p>
          </div>
        )}

        {/* ======================================================== */}
        {/* Modal de edição de permissões                             */}
        {/* ======================================================== */}
        {editingUser && (
          <div className="um-modal-backdrop" onClick={closeEditModal}>
            <div className="um-modal" onClick={(e) => e.stopPropagation()}>
              <div className="um-modal-header">
                <h2>
                  <i className="fas fa-key" /> Permissões de{' '}
                  <span className="um-modal-username">{editingUser.username}</span>
                </h2>
                <button type="button" className="um-modal-close" onClick={closeEditModal}>
                  <i className="fas fa-times" />
                </button>
              </div>

              <div className="um-modal-body">
                {/* Tipo de usuário */}
                <div className="um-perm-section">
                  <h3><i className="fas fa-shield-alt" /> Nível de Acesso</h3>
                  {callerIsSuperuser ? (
                    <div className="um-user-type-selector">
                      {[
                        { value: 'superuser', label: 'Superusuário', icon: 'fa-user-shield', desc: 'Acesso total ao sistema' },
                        { value: 'regular', label: 'Regular', icon: 'fa-user-tie', desc: 'Pode gerenciar usuários simples' },
                        { value: 'simple', label: 'Simples', icon: 'fa-user', desc: 'Acesso limitado ao seu escopo' },
                      ].map(opt => {
                        const currentType = editForm.isSuperuser ? 'superuser' : editForm.isSimple ? 'simple' : 'regular';
                        return (
                          <label key={opt.value} className={`um-type-option ${currentType === opt.value ? 'um-type-selected' : ''}`}>
                            <input
                              type="radio"
                              name="editUserType"
                              value={opt.value}
                              checked={currentType === opt.value}
                              onChange={() => setEditForm(prev => ({
                                ...prev,
                                isSuperuser: opt.value === 'superuser',
                                isSimple: opt.value === 'simple',
                              }))}
                            />
                            <i className={`fas ${opt.icon}`} />
                            <div>
                              <strong>{opt.label}</strong>
                              <small>{opt.desc}</small>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="um-perm-note">
                      <i className="fas fa-info-circle" /> Usuário simples — você pode definir as permissões abaixo dentro do seu escopo.
                    </p>
                  )}
                  {editForm.isSuperuser && (
                    <p className="um-perm-note">
                      <i className="fas fa-info-circle" /> Superusuários têm acesso a todas as operações, unidades e departamentos. As permissões abaixo serão ignoradas.
                    </p>
                  )}
                </div>

                {!editForm.isSuperuser && (
                  <>
                    {/* ---- Operações ---- */}
                    <div className="um-perm-section">
                      <div className="um-perm-section-header">
                        <h3><i className="fas fa-layer-group" /> Operações</h3>
                        <span className="um-perm-count">
                          {editForm.operationIds.length} selecionada(s)
                        </span>
                      </div>
                      <div className="um-section-toolbar">
                        <div className="um-search-inline">
                          <i className="fas fa-search" />
                          <input
                            type="text"
                            placeholder="Buscar operação..."
                            value={searchOps}
                            onChange={(e) => setSearchOps(e.target.value)}
                          />
                          {searchOps && (
                            <button type="button" className="um-search-clear" onClick={() => setSearchOps('')}>
                              <i className="fas fa-times" />
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          className="um-select-all-btn"
                          onClick={() => selectAllFiltered('operationIds', filteredOperations)}
                        >
                          <i className={`fas ${allFilteredSelected('operationIds', filteredOperations) ? 'fa-check-square' : 'fa-square'}`} />
                          {allFilteredSelected('operationIds', filteredOperations) ? 'Desmarcar' : 'Selecionar'} todos ({filteredOperations.length})
                        </button>
                      </div>
                      <div className="um-checkbox-grid">
                        {filteredOperations.map(op => (
                          <label key={op.id} className={`um-checkbox-label ${editForm.operationIds.includes(op.id) ? 'um-checked' : ''}`}>
                            <input
                              type="checkbox"
                              checked={editForm.operationIds.includes(op.id)}
                              onChange={() => toggleArrayItem('operationIds', op.id)}
                            />
                            <span>{op.name}</span>
                            {op.code && <small className="um-checkbox-code">{op.code}</small>}
                          </label>
                        ))}
                        {filteredOperations.length === 0 && (
                          <p className="um-perm-empty">
                            {searchOps ? 'Nenhuma operação encontrada' : 'Nenhuma operação ativa disponível'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* ---- Unidades ---- */}
                    <div className="um-perm-section">
                      <div className="um-perm-section-header">
                        <h3><i className="fas fa-map-marker-alt" /> Unidades</h3>
                        <span className="um-perm-count">
                          {editForm.unitIds.length} selecionada(s)
                          {editForm.operationIds.length > 0 && (
                            <span className="um-filter-badge">
                              <i className="fas fa-filter" /> filtrado por operações
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="um-section-toolbar">
                        <div className="um-search-inline">
                          <i className="fas fa-search" />
                          <input
                            type="text"
                            placeholder="Buscar unidade..."
                            value={searchUnits}
                            onChange={(e) => setSearchUnits(e.target.value)}
                          />
                          {searchUnits && (
                            <button type="button" className="um-search-clear" onClick={() => setSearchUnits('')}>
                              <i className="fas fa-times" />
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          className="um-select-all-btn"
                          onClick={() => selectAllFiltered('unitIds', filteredUnits)}
                        >
                          <i className={`fas ${allFilteredSelected('unitIds', filteredUnits) ? 'fa-check-square' : 'fa-square'}`} />
                          {allFilteredSelected('unitIds', filteredUnits) ? 'Desmarcar' : 'Selecionar'} todos ({filteredUnits.length})
                        </button>
                      </div>
                      <div className="um-checkbox-grid">
                        {filteredUnits.map(unit => (
                          <label key={unit.id} className={`um-checkbox-label ${editForm.unitIds.includes(unit.id) ? 'um-checked' : ''}`}>
                            <input
                              type="checkbox"
                              checked={editForm.unitIds.includes(unit.id)}
                              onChange={() => toggleArrayItem('unitIds', unit.id)}
                            />
                            <span>{unit.name}</span>
                            {unit.code && <small className="um-checkbox-code">{unit.code}</small>}
                          </label>
                        ))}
                        {filteredUnits.length === 0 && (
                          <p className="um-perm-empty">
                            {editForm.operationIds.length > 0
                              ? 'Nenhuma unidade vinculada às operações selecionadas'
                              : searchUnits
                                ? 'Nenhuma unidade encontrada'
                                : 'Nenhuma unidade ativa disponível'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* ---- Departamentos ---- */}
                    <div className="um-perm-section">
                      <div className="um-perm-section-header">
                        <h3><i className="fas fa-building" /> Departamentos</h3>
                        <span className="um-perm-count">
                          {editForm.departmentIds.length} selecionado(s)
                          {(editForm.operationIds.length > 0 || editForm.unitIds.length > 0) && (
                            <span className="um-filter-badge">
                              <i className="fas fa-filter" /> filtrado
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="um-section-toolbar">
                        <div className="um-search-inline">
                          <i className="fas fa-search" />
                          <input
                            type="text"
                            placeholder="Buscar departamento..."
                            value={searchDepts}
                            onChange={(e) => setSearchDepts(e.target.value)}
                          />
                          {searchDepts && (
                            <button type="button" className="um-search-clear" onClick={() => setSearchDepts('')}>
                              <i className="fas fa-times" />
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          className="um-select-all-btn"
                          onClick={() => selectAllFiltered('departmentIds', filteredDepartments)}
                        >
                          <i className={`fas ${allFilteredSelected('departmentIds', filteredDepartments) ? 'fa-check-square' : 'fa-square'}`} />
                          {allFilteredSelected('departmentIds', filteredDepartments) ? 'Desmarcar' : 'Selecionar'} todos ({filteredDepartments.length})
                        </button>
                      </div>
                      <div className="um-checkbox-grid">
                        {filteredDepartments.map(dept => (
                          <label key={dept.id} className={`um-checkbox-label ${editForm.departmentIds.includes(dept.id) ? 'um-checked' : ''}`}>
                            <input
                              type="checkbox"
                              checked={editForm.departmentIds.includes(dept.id)}
                              onChange={() => toggleArrayItem('departmentIds', dept.id)}
                            />
                            <span>{dept.name}</span>
                            {(dept.operation_name || dept.unit_name) && (
                              <small className="um-checkbox-code">
                                {[dept.operation_name, dept.unit_name].filter(Boolean).join(' / ')}
                              </small>
                            )}
                          </label>
                        ))}
                        {filteredDepartments.length === 0 && (
                          <p className="um-perm-empty">
                            {editForm.operationIds.length > 0 || editForm.unitIds.length > 0
                              ? 'Nenhum departamento corresponde às operações/unidades selecionadas'
                              : searchDepts
                                ? 'Nenhum departamento encontrado'
                                : 'Nenhum departamento ativo disponível'}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="um-modal-footer">
                <button type="button" className="btn dl-btn dl-btn-ghost" onClick={closeEditModal}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn dl-btn dl-btn-primary"
                  onClick={savePermissions}
                  disabled={saving}
                >
                  {saving ? (
                    <><span className="spinner-border spinner-border-sm" /> Salvando...</>
                  ) : (
                    <><i className="fas fa-save" /> Salvar Permissões</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ======================================================== */}
        {/* Modal de reset de senha                                   */}
        {/* ======================================================== */}
        {resetPasswordUser && (
          <div className="um-modal-backdrop" onClick={() => setResetPasswordUser(null)}>
            <div className="um-modal um-modal-sm" onClick={(e) => e.stopPropagation()}>
              <div className="um-modal-header">
                <h2>
                  <i className="fas fa-lock" /> Redefinir Senha
                </h2>
                <button type="button" className="um-modal-close" onClick={() => setResetPasswordUser(null)}>
                  <i className="fas fa-times" />
                </button>
              </div>

              <div className="um-modal-body">
                <p>Redefinir senha do usuário <strong>{resetPasswordUser.username}</strong>:</p>
                <div className="um-field">
                  <label className="form-label">Nova Senha</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Mínimo 8 caracteres"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              <div className="um-modal-footer">
                <button type="button" className="btn dl-btn dl-btn-ghost" onClick={() => setResetPasswordUser(null)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn dl-btn dl-btn-primary"
                  onClick={handleResetPassword}
                  disabled={saving || newPassword.length < 8}
                >
                  {saving ? (
                    <><span className="spinner-border spinner-border-sm" /> Salvando...</>
                  ) : (
                    <><i className="fas fa-save" /> Redefinir Senha</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ======================================================== */}
        {/* Modal de criação de usuário                               */}
        {/* ======================================================== */}
        {showCreateModal && (
          <div className="um-modal-backdrop" onClick={closeCreateModal}>
            <div className="um-modal" onClick={(e) => e.stopPropagation()}>
              <div className="um-modal-header">
                <h2>
                  <i className="fas fa-user-plus" /> Novo Usuário
                </h2>
                <button type="button" className="um-modal-close" onClick={closeCreateModal}>
                  <i className="fas fa-times" />
                </button>
              </div>

              <div className="um-modal-body">
                {/* Dados básicos */}
                <div className="um-perm-section">
                  <h3><i className="fas fa-id-card" /> Dados do Usuário</h3>
                  <div className="um-create-fields">
                    <div className="um-create-row">
                      <div className="um-field">
                        <label className="form-label">Username *</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="nome.sobrenome"
                          value={createForm.username}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, username: e.target.value }))}
                          autoFocus
                        />
                      </div>
                      <div className="um-field">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          placeholder="email@exemplo.com"
                          value={createForm.email}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="um-create-row">
                      <div className="um-field">
                        <label className="form-label">Nome</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Primeiro nome"
                          value={createForm.firstName}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, firstName: e.target.value }))}
                        />
                      </div>
                      <div className="um-field">
                        <label className="form-label">Sobrenome</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Sobrenome"
                          value={createForm.lastName}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, lastName: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="um-create-row">
                      <div className="um-field">
                        <label className="form-label">Senha *</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Mínimo 8 caracteres"
                          value={createForm.password}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tipo de usuário */}
                <div className="um-perm-section">
                  <h3><i className="fas fa-shield-alt" /> Nível de Acesso</h3>
                  {callerIsSuperuser ? (
                    <div className="um-user-type-selector">
                      {[
                        { value: 'superuser', label: 'Superusuário', icon: 'fa-user-shield', desc: 'Acesso total ao sistema' },
                        { value: 'regular', label: 'Regular', icon: 'fa-user-tie', desc: 'Pode gerenciar usuários simples' },
                        { value: 'simple', label: 'Simples', icon: 'fa-user', desc: 'Acesso limitado ao seu escopo' },
                      ].map(opt => {
                        const currentType = createForm.isSuperuser ? 'superuser' : createForm.isSimple ? 'simple' : 'regular';
                        return (
                          <label key={opt.value} className={`um-type-option ${currentType === opt.value ? 'um-type-selected' : ''}`}>
                            <input
                              type="radio"
                              name="createUserType"
                              value={opt.value}
                              checked={currentType === opt.value}
                              onChange={() => setCreateForm(prev => ({
                                ...prev,
                                isSuperuser: opt.value === 'superuser',
                                isSimple: opt.value === 'simple',
                              }))}
                            />
                            <i className={`fas ${opt.icon}`} />
                            <div>
                              <strong>{opt.label}</strong>
                              <small>{opt.desc}</small>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="um-perm-note">
                      <i className="fas fa-user" /> Este usuário será criado como <strong>Simples</strong> — com permissões dentro do seu escopo.
                    </p>
                  )}
                  {createForm.isSuperuser && (
                    <p className="um-perm-note">
                      <i className="fas fa-info-circle" /> Superusuários têm acesso a todas as operações, unidades e departamentos.
                    </p>
                  )}
                </div>

                {!createForm.isSuperuser && (
                  <>
                    {/* Operações */}
                    <div className="um-perm-section">
                      <div className="um-perm-section-header">
                        <h3><i className="fas fa-layer-group" /> Operações</h3>
                        <span className="um-perm-count">{createForm.operationIds.length} selecionada(s)</span>
                      </div>
                      <div className="um-section-toolbar">
                        <div className="um-search-inline">
                          <i className="fas fa-search" />
                          <input
                            type="text"
                            placeholder="Buscar operação..."
                            value={createSearchOps}
                            onChange={(e) => setCreateSearchOps(e.target.value)}
                          />
                          {createSearchOps && (
                            <button type="button" className="um-search-clear" onClick={() => setCreateSearchOps('')}>
                              <i className="fas fa-times" />
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          className="um-select-all-btn"
                          onClick={() => selectAllCreateFiltered('operationIds', createFilteredOperations)}
                        >
                          <i className={`fas ${allCreateFilteredSelected('operationIds', createFilteredOperations) ? 'fa-check-square' : 'fa-square'}`} />
                          {allCreateFilteredSelected('operationIds', createFilteredOperations) ? 'Desmarcar' : 'Selecionar'} todos ({createFilteredOperations.length})
                        </button>
                      </div>
                      <div className="um-checkbox-grid">
                        {createFilteredOperations.map(op => (
                          <label key={op.id} className={`um-checkbox-label ${createForm.operationIds.includes(op.id) ? 'um-checked' : ''}`}>
                            <input
                              type="checkbox"
                              checked={createForm.operationIds.includes(op.id)}
                              onChange={() => toggleCreateArrayItem('operationIds', op.id)}
                            />
                            <span>{op.name}</span>
                            {op.code && <small className="um-checkbox-code">{op.code}</small>}
                          </label>
                        ))}
                        {createFilteredOperations.length === 0 && (
                          <p className="um-perm-empty">
                            {createSearchOps ? 'Nenhuma operação encontrada' : 'Nenhuma operação ativa disponível'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Unidades */}
                    <div className="um-perm-section">
                      <div className="um-perm-section-header">
                        <h3><i className="fas fa-map-marker-alt" /> Unidades</h3>
                        <span className="um-perm-count">
                          {createForm.unitIds.length} selecionada(s)
                          {createForm.operationIds.length > 0 && (
                            <span className="um-filter-badge"><i className="fas fa-filter" /> filtrado por operações</span>
                          )}
                        </span>
                      </div>
                      <div className="um-section-toolbar">
                        <div className="um-search-inline">
                          <i className="fas fa-search" />
                          <input
                            type="text"
                            placeholder="Buscar unidade..."
                            value={createSearchUnits}
                            onChange={(e) => setCreateSearchUnits(e.target.value)}
                          />
                          {createSearchUnits && (
                            <button type="button" className="um-search-clear" onClick={() => setCreateSearchUnits('')}>
                              <i className="fas fa-times" />
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          className="um-select-all-btn"
                          onClick={() => selectAllCreateFiltered('unitIds', createFilteredUnits)}
                        >
                          <i className={`fas ${allCreateFilteredSelected('unitIds', createFilteredUnits) ? 'fa-check-square' : 'fa-square'}`} />
                          {allCreateFilteredSelected('unitIds', createFilteredUnits) ? 'Desmarcar' : 'Selecionar'} todos ({createFilteredUnits.length})
                        </button>
                      </div>
                      <div className="um-checkbox-grid">
                        {createFilteredUnits.map(unit => (
                          <label key={unit.id} className={`um-checkbox-label ${createForm.unitIds.includes(unit.id) ? 'um-checked' : ''}`}>
                            <input
                              type="checkbox"
                              checked={createForm.unitIds.includes(unit.id)}
                              onChange={() => toggleCreateArrayItem('unitIds', unit.id)}
                            />
                            <span>{unit.name}</span>
                            {unit.code && <small className="um-checkbox-code">{unit.code}</small>}
                          </label>
                        ))}
                        {createFilteredUnits.length === 0 && (
                          <p className="um-perm-empty">
                            {createForm.operationIds.length > 0
                              ? 'Nenhuma unidade vinculada às operações selecionadas'
                              : createSearchUnits ? 'Nenhuma unidade encontrada' : 'Nenhuma unidade ativa disponível'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Departamentos */}
                    <div className="um-perm-section">
                      <div className="um-perm-section-header">
                        <h3><i className="fas fa-building" /> Departamentos</h3>
                        <span className="um-perm-count">
                          {createForm.departmentIds.length} selecionado(s)
                          {(createForm.operationIds.length > 0 || createForm.unitIds.length > 0) && (
                            <span className="um-filter-badge"><i className="fas fa-filter" /> filtrado</span>
                          )}
                        </span>
                      </div>
                      <div className="um-section-toolbar">
                        <div className="um-search-inline">
                          <i className="fas fa-search" />
                          <input
                            type="text"
                            placeholder="Buscar departamento..."
                            value={createSearchDepts}
                            onChange={(e) => setCreateSearchDepts(e.target.value)}
                          />
                          {createSearchDepts && (
                            <button type="button" className="um-search-clear" onClick={() => setCreateSearchDepts('')}>
                              <i className="fas fa-times" />
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          className="um-select-all-btn"
                          onClick={() => selectAllCreateFiltered('departmentIds', createFilteredDepartments)}
                        >
                          <i className={`fas ${allCreateFilteredSelected('departmentIds', createFilteredDepartments) ? 'fa-check-square' : 'fa-square'}`} />
                          {allCreateFilteredSelected('departmentIds', createFilteredDepartments) ? 'Desmarcar' : 'Selecionar'} todos ({createFilteredDepartments.length})
                        </button>
                      </div>
                      <div className="um-checkbox-grid">
                        {createFilteredDepartments.map(dept => (
                          <label key={dept.id} className={`um-checkbox-label ${createForm.departmentIds.includes(dept.id) ? 'um-checked' : ''}`}>
                            <input
                              type="checkbox"
                              checked={createForm.departmentIds.includes(dept.id)}
                              onChange={() => toggleCreateArrayItem('departmentIds', dept.id)}
                            />
                            <span>{dept.name}</span>
                            {(dept.operation_name || dept.unit_name) && (
                              <small className="um-checkbox-code">
                                {[dept.operation_name, dept.unit_name].filter(Boolean).join(' / ')}
                              </small>
                            )}
                          </label>
                        ))}
                        {createFilteredDepartments.length === 0 && (
                          <p className="um-perm-empty">
                            {createForm.operationIds.length > 0 || createForm.unitIds.length > 0
                              ? 'Nenhum departamento corresponde às operações/unidades selecionadas'
                              : createSearchDepts ? 'Nenhum departamento encontrado' : 'Nenhum departamento ativo disponível'}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="um-modal-footer">
                <button type="button" className="btn dl-btn dl-btn-ghost" onClick={closeCreateModal}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn dl-btn dl-btn-primary"
                  onClick={handleCreateUser}
                  disabled={saving || !createForm.username.trim() || createForm.password.length < 8}
                >
                  {saving ? (
                    <><span className="spinner-border spinner-border-sm" /> Criando...</>
                  ) : (
                    <><i className="fas fa-user-plus" /> Criar Usuário</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
